// ScanConfig.jsx — "fields to scan" slideout + scan kickoff modal for the
// Existing Attachment Field Scan tool.
//
// The field list comes from the customer's FMX Equipment fields (synced via
// fmxSync); there is deliberately no way to add fields here. Toggling a
// field off hides it from review and future scans — it doesn't delete data.

import { useState } from 'react';
import { T } from '../../theme';
import { Button, IconButton } from '../fmxkit';
import { ScanToggle, ScanFilterChip, AcceptCheck, formatRelative } from './scanShared';

// Rough per-attachment Claude vision cost used for the kickoff estimate
// (one call per attachment; nameplate photo ≈ 1.5k in / 300 out tokens).
export const EST_COST_PER_ATTACHMENT = 0.0085;

export function ScanFieldsSlideout({ fields, scannedCount, siteUrl, syncedAt, onToggle, onClose }) {
  const groups = ['Standard', 'Custom fields'];
  const onCount = fields.filter(f => f.on).length;
  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 210, background: 'rgba(0,0,0,0.4)', display: 'flex', justifyContent: 'flex-end' }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 420, maxWidth: '92vw', height: '100%', background: T.white, boxShadow: T.shadowLg, display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px 14px', borderBottom: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'flex-start', gap: 10 }}>
          <div>
            <div style={{ font: `700 17px/23px ${T.fontSans}`, color: T.ink }}>Fields to scan for</div>
            <div style={{ font: `400 12.5px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>
              {onCount} fields on · applies to this project's scans
            </div>
            <div style={{ display: 'inline-flex', alignItems: 'center', gap: 6, marginTop: 7, font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft }}>
              <i className="fa-solid fa-rotate" style={{ fontSize: 10 }} />
              Equipment fields synced from {siteUrl || 'FMX'}{syncedAt ? ` · ${formatRelative(syncedAt)}` : ''}
            </div>
          </div>
          <IconButton icon="xmark" title="Close" onClick={onClose} style={{ marginLeft: 'auto' }} />
        </div>
        <div style={{ flex: 1, overflow: 'auto', padding: '6px 0 12px' }}>
          {groups.map(g => {
            const rows = fields.filter(f => f.group === g);
            if (!rows.length) return null;
            return (
              <div key={g}>
                <div style={{ font: `700 11px/16px ${T.fontSans}`, color: T.inkSoft, textTransform: 'uppercase', letterSpacing: '0.06em', padding: '14px 20px 5px' }}>{g}</div>
                {rows.map(f => (
                  <div key={f.id} style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '9px 20px', opacity: f.on ? 1 : 0.55 }}>
                    <ScanToggle on={f.on} onClick={() => onToggle(f.id)} />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ font: `600 13.5px/18px ${T.fontSans}`, color: T.ink }}>{f.label}</div>
                      <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkSoft }}>
                        {f.kind === 'custom' && f.fieldType ? `${f.fieldType} · ` : ''}
                        found on {f.found} of {scannedCount} scanned
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            );
          })}
          {fields.length === 0 && (
            <div style={{ padding: '22px 20px', font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft }}>
              No equipment fields found — check the FMX connection in Settings.
            </div>
          )}
        </div>
        <div style={{ padding: '12px 20px', borderTop: `1px solid ${T.borderSubtle}`, display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, flex: 1 }}>
            This list comes from the customer's Equipment custom fields in FMX — add fields there and they appear here.
            Toggling a field off hides it from review; it doesn't delete data.
          </span>
          <Button variant="primary" size="sm" onClick={onClose}>Done</Button>
        </div>
      </div>
    </div>
  );
}

// Kickoff modal with a record-scope picker: filter the unscanned list by
// type/building/tag and check exactly which records the batch should cover
// (all selected by default). The scan only ever starts from the button here.
// `targets`: [{ id, tag, type, bld, attachmentCount }] · onStart(selectedIdSet)
export function ScanKickoffModal({ fields, targets, onEditFields, onStart, onClose }) {
  const [typeFilter, setTypeFilter] = useState(null);
  const [bldFilter, setBldFilter] = useState(null);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(() => new Set(targets.map(t => t.id)));

  const matches = (t) =>
    (!typeFilter || t.type === typeFilter) &&
    (!bldFilter || t.bld === bldFilter) &&
    (!q || t.tag.toLowerCase().includes(q.toLowerCase()));
  const shown = targets.filter(matches);
  const filtersOn = !!(typeFilter || bldFilter || q);
  const countBy = (fn, sel) => {
    const m = {};
    targets.forEach(t => { if (!sel || sel(t)) m[fn(t)] = (m[fn(t)] || 0) + 1; });
    return Object.keys(m).filter(Boolean).sort().map(name => ({ name, count: m[name] }));
  };
  const typeOptions = countBy(t => t.type, t => !bldFilter || t.bld === bldFilter);
  const bldOptions = countBy(t => t.bld, t => !typeFilter || t.type === typeFilter);

  const toggle = (id) => setSelected(prev => {
    const next = new Set(prev);
    if (next.has(id)) next.delete(id); else next.add(id);
    return next;
  });
  const selectAllShown = () => setSelected(prev => {
    const next = new Set(prev);
    for (const t of shown) next.add(t.id);
    return next;
  });
  const clearSelection = () => setSelected(new Set());

  const selTargets = targets.filter(t => selected.has(t.id));
  const selAttachments = selTargets.reduce((n, t) => n + t.attachmentCount, 0);
  const on = fields.filter(f => f.on);
  const estCost = selAttachments * EST_COST_PER_ATTACHMENT;
  const rowStyle = { display: 'flex', alignItems: 'center', gap: 10, padding: '10px 12px', background: T.bgMuted, borderRadius: T.radiusBtn };

  return (
    <div style={{ position: 'fixed', inset: 0, zIndex: 205, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }} onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ width: 560, maxWidth: '94vw', maxHeight: '90vh', background: T.white, borderRadius: T.radiusTable, boxShadow: T.shadowLg, overflow: 'hidden', display: 'flex', flexDirection: 'column' }}>
        <div style={{ padding: '16px 20px 0' }}>
          <div style={{ font: `700 17px/23px ${T.fontSans}`, color: T.ink }}>Scan unscanned equipment</div>
          <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, marginTop: 3 }}>
            Choose which records to scan. Claude reads each attachment once and queues results for your review — nothing writes to FMX until you approve it.
          </div>
        </div>

        {/* scope picker */}
        <div style={{ padding: '12px 20px 0' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <div style={{ position: 'relative', flex: 1, minWidth: 0 }}>
              <i className="fa-solid fa-magnifying-glass" style={{ position: 'absolute', left: 9, top: 8, color: T.inkSoft, fontSize: 11 }} />
              <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search tag…"
                style={{ width: '100%', height: 27, padding: '0 9px 0 26px', border: `1px solid ${T.border}`, borderRadius: T.radiusPill, font: `400 12.5px/18px ${T.fontSans}`, outline: 'none', background: T.bg, boxSizing: 'border-box' }} />
            </div>
            <div style={{ width: 140 }}><ScanFilterChip label="Type" value={typeFilter} options={typeOptions} onChange={setTypeFilter} /></div>
            <div style={{ width: 150 }}><ScanFilterChip label="Building" value={bldFilter} options={bldOptions} onChange={setBldFilter} /></div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, margin: '8px 0 6px', font: `400 11.5px/15px ${T.fontSans}`, color: T.inkMuted }}>
            <span onClick={selectAllShown} style={{ font: `600 11.5px/15px ${T.fontSans}`, color: T.brand, cursor: 'pointer' }}>Select all shown</span>
            <span onClick={clearSelection} style={{ font: `600 11.5px/15px ${T.fontSans}`, color: T.brand, cursor: 'pointer' }}>Clear selection</span>
            {filtersOn && (
              <span onClick={() => { setTypeFilter(null); setBldFilter(null); setQ(''); }} style={{ font: `600 11.5px/15px ${T.fontSans}`, color: T.brand, cursor: 'pointer' }}>
                <i className="fa-solid fa-filter-circle-xmark" style={{ fontSize: 10, marginRight: 4 }} />Clear filters
              </span>
            )}
            <span style={{ marginLeft: 'auto' }}>{selected.size} of {targets.length} selected{filtersOn ? ` · ${shown.length} shown` : ''}</span>
          </div>
          <div style={{ border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusBtn, maxHeight: 220, overflow: 'auto' }}>
            {shown.map((t, i) => (
              <div key={t.id} onClick={() => toggle(t.id)} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '6px 10px', cursor: 'pointer',
                borderBottom: i < shown.length - 1 ? `1px solid ${T.borderSubtle}` : 'none',
                background: selected.has(t.id) ? T.white : T.bg, opacity: selected.has(t.id) ? 1 : 0.65,
              }}>
                <AcceptCheck on={selected.has(t.id)} onClick={() => {}} size={16} />
                <span style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 140 }}>{t.tag}</span>
                <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>{t.type}</span>
                <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis', maxWidth: 150 }}>{t.bld}</span>
                <span style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkSoft, whiteSpace: 'nowrap' }}>
                  <i className="fa-solid fa-paperclip" style={{ fontSize: 9, marginRight: 3 }} />{t.attachmentCount}
                </span>
              </div>
            ))}
            {shown.length === 0 && (
              <div style={{ padding: '18px 12px', textAlign: 'center', font: `400 12.5px/17px ${T.fontSans}`, color: T.inkSoft }}>
                {targets.length ? 'No unscanned records match these filters.' : 'Nothing left to scan — every equipment with attachments has results.'}
              </div>
            )}
          </div>
        </div>

        <div style={{ padding: '12px 20px 4px', display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={rowStyle}>
            <i className="fa-solid fa-paperclip" style={{ color: T.inkMuted, fontSize: 13, width: 18, textAlign: 'center' }} />
            <span style={{ font: `500 13px/18px ${T.fontSans}`, color: T.ink }}>{selAttachments} attachments across {selTargets.length} equipment</span>
            <span style={{ marginLeft: 'auto', font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft }}>photos + PDFs</span>
          </div>
          <div style={{ ...rowStyle, alignItems: 'flex-start' }}>
            <i className="fa-solid fa-list-check" style={{ color: T.inkMuted, fontSize: 13, width: 18, textAlign: 'center', marginTop: 3 }} />
            <div style={{ flex: 1 }}>
              <span style={{ font: `500 13px/18px ${T.fontSans}`, color: T.ink }}>{on.length} fields selected</span>
              <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 1 }}>
                {on.slice(0, 5).map(f => f.label).join(' · ')}{on.length > 5 ? ` · +${on.length - 5} more` : ''}
              </div>
            </div>
            <span onClick={onEditFields} style={{ font: `600 12.5px/18px ${T.fontSans}`, color: T.brand, cursor: 'pointer', whiteSpace: 'nowrap' }}>Edit fields</span>
          </div>
          <div style={rowStyle}>
            <i className="fa-solid fa-dollar-sign" style={{ color: T.inkMuted, fontSize: 13, width: 18, textAlign: 'center' }} />
            <span style={{ font: `500 13px/18px ${T.fontSans}`, color: T.ink }}>Estimated cost ~${estCost.toFixed(2)}</span>
            <span style={{ marginLeft: 'auto', font: `400 12px/16px ${T.fontSans}`, color: T.inkSoft }}>billed to this project</span>
          </div>
        </div>
        <div style={{ padding: '14px 20px 16px', display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
          <Button variant="neutral" onClick={onClose}>Cancel</Button>
          <Button variant="primary" icon="camera" onClick={() => onStart(selected)} disabled={!on.length || !selected.size}>
            Start scan{selected.size ? ` (${selected.size})` : ''}
          </Button>
        </div>
      </div>
    </div>
  );
}
